import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { getSignSession, submitSignature } from '../../api/signApi';
import SignaturePad from '../common/signaturePad';

const PP = "'Poppins', sans-serif";
const API_BASE_ORIGIN = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');

// ── Tiny helpers ──────────────────────────────────────────────────────────────

function formatRM(value) {
    if (value == null) return '';
    return 'RM ' + Number(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Form preview row ───────────────────────────────────────────────────────────

function FormPreviewRow({ title, fileUrl }) {
    const [expanded, setExpanded] = useState(false);
    const absoluteUrl = fileUrl ? `${API_BASE_ORIGIN}${fileUrl}` : null;
    const label = title.replace('Form: ', '');

    return (
        <Box sx={{ mb: 1.5 }}>
            <Box
                onClick={() => setExpanded(e => !e)}
                sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 1.5, py: 1, borderRadius: '8px', border: '1px solid #E2E8F0',
                    background: '#F8FAFC', cursor: 'pointer',
                    '&:hover': { background: '#F1F5F9' }
                }}
            >
                <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#0F172A', fontWeight: 500 }}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                </Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8' }}>
                    {expanded ? 'Close' : 'Preview'}
                </Typography>
            </Box>
            {expanded && absoluteUrl && (
                <Box sx={{ mt: 0.75, borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                    <embed
                        src={absoluteUrl}
                        type="application/pdf"
                        width="100%"
                        height="480px"
                        style={{ display: 'block' }}
                    />
                </Box>
            )}
        </Box>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SignPage() {
    const { token } = useParams();
    const [status, setStatus] = useState('loading'); // loading | invalid | expired | signed | ok | success
    const [session, setSession] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const canvasApiRef = useRef(null);
    const salesSigApiRef = useRef(null);

    useEffect(() => {
        if (!token) { setStatus('invalid'); return; }
        getSignSession(token)
            .then(res => {
                const data = res.data;
                setStatus(data.status);
                if (data.status === 'ok') setSession(data);
            })
            .catch(() => setStatus('invalid'));
    }, [token]);

    const handleSubmit = async () => {
        setSubmitError('');

        const signaturePng = canvasApiRef.current?.getDataUrl?.() || '';

        // Empty check uses the drawing flag tracked while strokes happen — reliable
        // for thin signatures (pixel-averaging a 600x200 canvas down to 1px loses them).
        if (canvasApiRef.current?.isEmpty?.() || !signaturePng || signaturePng === 'data:image/png;base64,') {
            setSubmitError('The signature pad is empty. Please draw your signature.');
            return;
        }

        // Optional sales-person signature (only if drawn).
        const salesSig = (salesSigApiRef.current && !salesSigApiRef.current.isEmpty?.())
            ? salesSigApiRef.current.getDataUrl?.()
            : null;

        setSubmitting(true);
        try {
            await submitSignature(token, { signaturePng, signedByName: (session?.customerName || '').trim(), salesPersonSignaturePng: salesSig });
            setStatus('success');
        } catch (err) {
            const msg = err?.response?.data?.message
                || (typeof err?.response?.data === 'string' ? err.response.data : null)
                || 'Submission failed. Please try again.';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading ────────────────────────────────────────────────────────────────

    if (status === 'loading') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // ── Terminal states ────────────────────────────────────────────────────────

    if (status === 'invalid') {
        return (
            <PageShell>
                <StatusCard
                    title="Link not found"
                    body="This signing link is invalid or has already been used. Please contact your solar consultant for a new link."
                    color="#EF4444"
                />
            </PageShell>
        );
    }

    if (status === 'expired') {
        return (
            <PageShell>
                <StatusCard
                    title="Link expired"
                    body="This signing link has expired (links are valid for 14 days). Please contact your solar consultant to request a new link."
                    color="#F59E0B"
                />
            </PageShell>
        );
    }

    if (status === 'signed') {
        return (
            <PageShell>
                <StatusCard
                    title="Already signed"
                    body="This agreement has already been signed. Thank you for completing your solar agreement."
                    color="#22C55E"
                />
            </PageShell>
        );
    }

    if (status === 'success') {
        return (
            <PageShell>
                <StatusCard
                    title="Thank you, signed"
                    body="Your signature has been recorded and your agreement documents are being generated. Your solar consultant will be in touch shortly."
                    color="#22C55E"
                />
            </PageShell>
        );
    }

    // ── Active signing session ─────────────────────────────────────────────────

    const { customerName, kwp, panels, finalPrice, forms } = session || {};

    return (
        <PageShell>
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.15rem', color: '#0F172A', mb: 0.25 }}>
                    Solar Agreement
                </Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B' }}>
                    Please review the documents below, draw your signature, and enter your full name to confirm.
                </Typography>
            </Box>

            {/* System summary */}
            <Box sx={{
                background: '#F8FAFC', border: '1px solid #E2E8F0',
                borderRadius: '10px', px: 2, py: 1.5, mb: 2.5
            }}>
                {customerName ? (
                    <Row label="Customer" value={customerName} />
                ) : null}
                {kwp ? <Row label="System size" value={`${kwp} kWp`} /> : null}
                {panels ? <Row label="Panels" value={`${panels} panels`} /> : null}
                {finalPrice ? <Row label="Final price" value={formatRM(finalPrice)} /> : null}
            </Box>

            {/* Form previews */}
            {forms && forms.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{
                        fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                        color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1
                    }}>
                        Documents to sign
                    </Typography>
                    {forms.map((f, i) => (
                        <FormPreviewRow key={i} title={f.title} fileUrl={f.fileUrl} />
                    ))}
                </Box>
            )}

            {/* Customer signature pad */}
            <Box sx={{ mb: 2 }}>
                <Typography sx={{
                    fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                    color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                }}>
                    Customer signature
                </Typography>
                <SignaturePad onReady={(api) => { canvasApiRef.current = api; }} />
            </Box>

            {/* Sales person signature pad (optional — drawn alongside the customer) */}
            <Box sx={{ mb: 2 }}>
                <Typography sx={{
                    fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                    color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                }}>
                    Sales person signature
                </Typography>
                <SignaturePad onReady={(api) => { salesSigApiRef.current = api; }} />
            </Box>

            {/* Error */}
            {submitError && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#EF4444', mb: 1.25 }}>
                    {submitError}
                </Typography>
            )}

            {/* Submit */}
            <Button
                fullWidth
                variant="contained"
                disabled={submitting}
                onClick={handleSubmit}
                sx={{
                    fontFamily: PP, textTransform: 'none', fontWeight: 600,
                    fontSize: '0.9rem', borderRadius: '10px', py: 1.25,
                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                    boxShadow: 'none',
                }}
            >
                {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign and Submit'}
            </Button>

            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8', textAlign: 'center', mt: 1.5 }}>
                By submitting you confirm that you have read and agree to the documents above.
            </Typography>
        </PageShell>
    );
}

// ── Layout helpers ─────────────────────────────────────────────────────────────

function PageShell({ children }) {
    return (
        <Box sx={{
            minHeight: '100vh', background: '#F1F5F9',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            px: 1.5, py: 3
        }}>
            <Box sx={{
                width: '100%', maxWidth: 520,
                background: '#fff', borderRadius: '14px',
                boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
                px: { xs: 2, sm: 3 }, py: 3
            }}>
                {/* Header */}
                <Box sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8' }}>
                        Secure Document Signing
                    </Typography>
                </Box>
                {children}
            </Box>
        </Box>
    );
}

function StatusCard({ title, body, color }) {
    return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{
                width: 48, height: 48, borderRadius: '50%',
                background: color + '20', display: 'flex', alignItems: 'center',
                justifyContent: 'center', mx: 'auto', mb: 1.5
            }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', background: color }} />
            </Box>
            <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A', mb: 0.75 }}>
                {title}
            </Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', lineHeight: 1.6 }}>
                {body}
            </Typography>
        </Box>
    );
}

function Row({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B' }}>{label}</Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#0F172A', fontWeight: 500 }}>{value}</Typography>
        </Box>
    );
}
